import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNumFormatter } from '~/utils/format';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ClickPosition } from '~/utils/click-position';
import { ModuleType } from '~/utils/module-type';
import useReflowClick from '~/utils/use-reflow-click';
import { useActiveToArticle } from '~/utils/active/use-active-to-article';
import Bookmark from 'icon/bookmark.svg';
import Heart from 'icon/heart.svg';
import EllipsisBubble from 'icon/ellipsis_bubble.svg';
import { PostDetailModalAtoms } from '../../atoms';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';

import './style.scss';

export const ArticleInfoBar: FC = ({}) => {
  const { t } = useTranslation();
  const formatNum = useNumFormatter();

  const article = useScopedAtomValue(PostDetailModalAtoms.$CurrentArticle);
  const positionPrefix = useScopedAtomValue($CurrentPositionPrefix);
  const activeToArticle = useActiveToArticle(article);

  const { commentCount, diggCount, favoriteCount } = article;

  const savedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: positionPrefix + ModuleType.main_content,
        current_position: positionPrefix + ClickPosition.main_favorite_icon,
        group_id: article.groupId,
      });
    }, []),
  });

  const handleSavedClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToArticle([positionPrefix, ClickPosition.main_favorite_icon]);
      },
      defaultBehavior: () => {
        activeToArticle([positionPrefix, ClickPosition.main_favorite_icon]);
      },
      clickEvent: () => ({
        current_position: positionPrefix + ClickPosition.main_favorite_icon,
      }),
    },
    [activeToArticle],
  );

  const diggRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: positionPrefix + ModuleType.main_content,
        current_position: positionPrefix + ClickPosition.main_like_icon,
        group_id: article.groupId,
      });
    }, []),
  });

  const handleDiggClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToArticle([positionPrefix, ClickPosition.main_like_icon]);
      },
      defaultBehavior: () => {
        activeToArticle([positionPrefix, ClickPosition.main_like_icon]);
      },
      clickEvent: () => ({
        current_position: positionPrefix + ClickPosition.main_like_icon,
      }),
    },
    [activeToArticle],
  );

  const commentedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: positionPrefix + ModuleType.main_content,
        current_position: positionPrefix + ClickPosition.main_comment_icon,
        group_id: article.groupId,
      });
    }, []),
  });

  const handleCommentedClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToArticle([positionPrefix, ClickPosition.main_comment_icon]);
      },
      defaultBehavior: () => {
        activeToArticle([positionPrefix, ClickPosition.main_comment_icon]);
      },
      clickEvent: () => ({
        current_position: positionPrefix + ClickPosition.main_comment_icon,
      }),
    },
    [activeToArticle],
  );

  return (
    <div className="post-detail-modal-article-info">
      <div className="saved" ref={savedRef} onClick={handleSavedClick}>
        <Bookmark />
        <span>{t('saved_text').replace('%d', formatNum(favoriteCount))}</span>
      </div>

      <div ref={diggRef} onClick={handleDiggClick}>
        <Heart />
        {Boolean(diggCount) && <span>{formatNum(diggCount)}</span>}
      </div>

      <div ref={commentedRef} onClick={handleCommentedClick}>
        <EllipsisBubble />
        {Boolean(commentCount) && <span>{formatNum(commentCount)}</span>}
      </div>
    </div>
  );
};
